import React from "react"
import { useId } from "@reach/auto-id"
import { isNotEmpty } from "utils/validation"
import { useTranslation } from "react-i18next"
import { FormFields, FormRule, FormError } from "models"
import {
	handleInternalInputValidation,
	FormFieldFloatingLabel,
	StatesAndHelpersFloatingLabel,
} from "../formUtils"
import { Errors } from "../Errors"

export type InputNumberProps = FormFields & {
	value: string
	unit?: string
	classNameInput?: string
}

export const InputNumber: React.FC<InputNumberProps> = React.forwardRef(
	(
		{
			// Attributes
			id,
			value = "",
			name,
			label = "",
			placeholder = "",
			unit = "",
			disabled,
			readonly = false,
			// Styles
			style,
			className = "",
			classNameLabel = "",
			classNameInput = "",
			forceFloating = false,
			// Error management
			required = false,
			customRules,
			showErrors,
			// Handler
			onChange,
			onFocus,
			onBlur,
			onValidate,
		},
		ref: React.Ref<HTMLInputElement>
	) => {
		const INPUT_TYPE = "number"
		const prefix = useId()
		const inputId = id ?? `${prefix}-${name}`
		const errorId = `${inputId}-error`

		const inputRef = React.useRef(null)
		// React.useImperativeHandle(
		// 	ref as React.Ref<Partial<HTMLInputElement>>,
		// 	() => ({
		// 		// if a parent component want be able to access inner ref with
		// 		// a forwadedRef and for instance call inputRef.current.focus()
		// 		// See https://reactjs.org/docs/hooks-reference.html#useimperativehandle
		// 		focus: () => inputRef.current.focus(),
		// 	})
		// )
		// const inputRef = ref ?? React.useRef(null)

		const [errors, setErrors] = React.useState<FormError[]>([])
		const { t } = useTranslation()

		const rules: FormRule[] = React.useMemo(
			() => [
				{
					key: "input-empty",
					check: (value: boolean) => (required ? isNotEmpty(value) : true),
					message: t("error_mandatory_field", { ns: "form", fieldName: label }),
				},
				...(customRules || []),
			],
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[required]
		)
		// customRules in dependencies array trigger an infinite loop,
		// because custom rules include a check property defining a new function,
		// preventing useMemo to return a valid shallow-comparison
		// or even useDeepMemo to return a valid deep-comparison

		const validate = React.useCallback(
			(currentValue: string) =>
				handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
			[rules, name, onValidate]
		)

		// Validate on mount and on each value change
		React.useEffect(() => {
			validate(value)
			return () => {
				// On unmounted errors and validation are no longer needed
				setErrors([])
				if (onValidate) onValidate(name, [])
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [value])

		return (
			<FormFieldFloatingLabel
				inputType={INPUT_TYPE}
				inputValue={value}
				forceFloating={forceFloating}
			>
				{({
					floatingLabelClassname,
					onFloatingLabelFocus,
					onFloatingLabelBlur,
					inputAttributeToFixAutofill,
				}: StatesAndHelpersFloatingLabel) => (
					<div
						className={`c-form-field ${className} ${floatingLabelClassname}`}
						style={style}
					>
						<label
							className={`c-form-field__label c-label ${classNameLabel}`}
							htmlFor={inputId}
						>
							{label} {required && "*"}
						</label>
						{unit && <span className="">{unit}</span>}

						<input
							className={`c-form-field__form c-input px-5 ${classNameInput} ${
								showErrors && errors.length > 0 ? "has-error " : ""
							}`}
							id={inputId}
							ref={inputRef}
							name={name}
							type={INPUT_TYPE}
							placeholder={placeholder}
							readOnly={readonly}
							value={value}
							disabled={disabled}
							required={required}
							onChange={(event) => {
								!readonly && onChange && onChange(event)
							}}
							onFocus={(event) => {
								onFloatingLabelFocus()
								onFocus && onFocus(event)
							}}
							onBlur={(event) => {
								onFloatingLabelBlur()
								onBlur && onBlur(event)
							}}
							// Disable Up and Down Arrows on Keyboard
							onWheel={() => inputRef.current.blur()}
							onKeyDown={(event) => {
								const keyCode = event.keyCode || event.which
								if (keyCode === 38 || keyCode === 40) {
									event.preventDefault()
								} else {
									return
								}
							}}
							{...inputAttributeToFixAutofill}
							aria-describedby={errorId}
							aria-invalid={Boolean(errors.length)}
						/>

						{/* Error messages */}
						{showErrors && <Errors id={errorId} errors={errors} />}
					</div>
				)}
			</FormFieldFloatingLabel>
		)
	}
)
